/* eslint-disable no-restricted-imports */
import { Autocomplete as MUIAucomplete, TextField } from '@mui/material';

import { ReactNode, useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form';

import { I18nResources, useI18n } from '@app-velocity/i18n';

export type AutocompleteProps<T extends FieldValues> = {
  options: string[];
  control: Control<T>;
  formErrors: FieldErrors<T>;
  placeholder: I18nResources;
  name: Path<T>;
  renderOption: (option: string) => ReactNode;
  onFocus?: () => void;
  onBlur?: () => void;
  phCaptureId?: string;
  getOptionLabel?: (option: string) => string;
  noOptionsText?: ReactNode;
  allowCustomValue?: boolean;
  disablePortal?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  size?: 'small' | 'medium';
  maxListHeight?: number;
  optionMinHeight?: number;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onValueChange?: (value: string | null) => void;
  loading?: boolean;
  disableClientFiltering?: boolean;
  popperZIndex?: number;
};

export const Autocomplete = <T extends FieldValues>({
  name,
  control,
  formErrors,
  options,
  phCaptureId,
  onFocus,
  onBlur,
  renderOption,
  placeholder,
  getOptionLabel,
  noOptionsText,
  allowCustomValue = true,
  disablePortal = true,
  disabled,
  multiline = true,
  size,
  maxListHeight,
  optionMinHeight,
  inputValue,
  onInputChange,
  onValueChange,
  loading,
  disableClientFiltering = false,
  popperZIndex,
}: AutocompleteProps<T>) => {
  const { t } = useI18n();

  const errorText = useMemo(() => {
    const fieldError = formErrors?.[name];
    const message = fieldError?.message;

    if (typeof message !== 'string') return null;

    return message;
  }, [formErrors, name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <MUIAucomplete
          freeSolo={allowCustomValue}
          disableClearable={allowCustomValue}
          disablePortal={disablePortal}
          disabled={disabled}
          id={name}
          options={options}
          value={field.value || null}
          inputValue={inputValue}
          getOptionLabel={getOptionLabel}
          noOptionsText={noOptionsText}
          loading={loading}
          filterOptions={
            disableClientFiltering ? (values) => values : undefined
          }
          onChange={(_, value) => {
            field.onChange(value);
            onValueChange?.(value);
          }}
          onInputChange={(_, value, reason) => {
            if (reason === 'input') {
              field.onChange(allowCustomValue ? value : null);
            }

            if (reason === 'input') onInputChange?.(value);
          }}
          onFocus={onFocus}
          onBlur={() => {
            field.onBlur();
            onBlur?.();
          }}
          slotProps={{
            popper: {
              sx: {
                ...(popperZIndex !== undefined
                  ? { zIndex: popperZIndex }
                  : {}),
                ...(disablePortal
                  ? {
                      position: 'relative !important',
                      transform: 'none !important',
                    }
                  : {}),
              },
            },
            paper: {
              sx: {
                borderRadius: 2,
                padding: 2,
                borderTopRightRadius: 0,
                borderTopLeftRadius: 0,
                margin: 0,
                boxShadow: 'none',
                background: '#fff',
                border: '1px solid var(--stroke-stroke-primary-color)',
                borderTop: 'none',
                '.MuiAutocomplete-listbox': {
                  maxHeight: maxListHeight,
                  overflowY: maxListHeight ? 'auto' : undefined,
                  overscrollBehavior: 'contain',
                  padding: 0,
                  '.MuiAutocomplete-option': {
                    borderRadius: 2,
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    gap: 2,
                    padding: 2,
                    fontSize: 13,
                    minHeight: optionMinHeight ?? 'unset',
                    overflow: 'hidden',
                    fontWeight: 300,
                    color: 'var(--input-placeholder-color)',
                    svg: {
                      fill: 'var(--input-placeholder-color)',
                    },
                    '&[aria-selected="true"]': {
                      background: 'none',
                    },
                    '&:hover': {
                      color: 'var(--text-text-primary-color)',
                      svg: {
                        fill: 'var(--info-info-icon-primary-color)',
                      },
                    },
                  },
                },
              },
            },
          }}
          renderOption={({ key, ...optionProps }, option) => (
            <li key={key} {...optionProps}>
              {renderOption(option)}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name={name}
              fullWidth
              multiline={multiline}
              size={size}
              inputProps={{
                ...params.inputProps,
                'data-ph-capture-attribute-id': phCaptureId,
              }}
              error={!!formErrors[name]}
              helperText={errorText as string | null}
              placeholder={t(placeholder)}
            />
          )}
        />
      )}
    />
  );
};
